import { createAsyncThunk } from '@reduxjs/toolkit';
// import { RootState } from '@castlery/shared-redux-store';
// import { selectProduct, selectVariant } from '@castlery/modules-product-domain';
import { EVENTS_NAMES_MAP } from '../events-name';
import { fbConversionTrack, getEventRandomId } from '../utils';
import { INTL_CURRENCY } from '@castlery/config';
import { TRACKING_MSGS_MAP, trackingFeatureService } from '../helpers';
import { ExtraArgument } from '@castlery/shared-redux-extra';
import { logger } from '@castlery/observability';

/**
 * @description 跟踪facebook view content事件
 * @param payload
 * @param fulfillWithValue
 * @param dispatch
 * @returns
 */
export const trackFacebookViewContentEvent = createAsyncThunk(
  'tracking/trackFbCapiViewContent',
  async (
    payload: {
      eventId: string;
      originalPrice: string;
      variant: {
        name: string;
        sku: string;
      };
    },
    { fulfillWithValue, dispatch }
  ) => {
    try {
      if (!payload.originalPrice || !payload.variant || !payload.eventId) {
        return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
      }

      const params = {
        eventName: EVENTS_NAMES_MAP.FB_CAPI_VIEW_CONTENT,
        eventId: payload.eventId,
        customData: {
          value: payload.originalPrice,
          currency: INTL_CURRENCY,
          content_name: payload.variant.name,
          content_ids: [payload.variant.sku],
          content_type: 'product',
        },
      };
      await dispatch(fbConversionTrack(params));
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (e) {
      logger.error('trackFbCapiViewContentEvent failed', { error: e });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);

/**
 * @description 跟踪facebook add to cart事件
 * @param payload
 * @param fulfillWithValue
 * @param dispatch
 * @returns
 */
export const trackFacebookAddToCartEvent = createAsyncThunk(
  'tracking/trackFbCapiAddToCart',
  async (
    payload: { variant: { name: string; sku: string }; originalPrice: string; eventId: string },
    { fulfillWithValue, dispatch }
  ) => {
    try {
      if (!payload.variant || !payload.originalPrice || !payload.eventId) {
        return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
      }

      const params = {
        eventName: EVENTS_NAMES_MAP.FB_CAPI_ADD_TO_CART,
        eventId: payload.eventId,
        customData: {
          value: payload.originalPrice,
          currency: INTL_CURRENCY,
          content_name: payload.variant.name,
          content_ids: [payload.variant.sku],
          content_type: 'product',
        },
      };
      await dispatch(fbConversionTrack(params));
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (e) {
      logger.error('trackFbCapiAddToCartEvent failed', { error: e });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);

/**
 * @description 跟踪facebook add swatch to cart事件
 * @param payload
 * @param fulfillWithValue
 * @param dispatch
 * @returns
 */
export const trackFacebookAddSwatchToCartEvent = createAsyncThunk(
  'tracking/trackFbCapiAddSwatchToCart',
  async (
    payload: { eventId: string; variant: { name: string; sku: string }; swatchRelatedProductId: number | undefined },
    { fulfillWithValue, dispatch }
  ) => {
    try {
      if (!payload.variant || !payload.swatchRelatedProductId || !payload.eventId) {
        return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
      }

      const params = {
        eventName: EVENTS_NAMES_MAP.FB_CAPI_CUSTOM_SWATCH_ATC,
        eventId: payload.eventId,
        customData: {
          content_category: 'Swatch',
          content_ids: [payload.variant.sku],
          content_name: payload.variant.name,
          related_product_id: payload.swatchRelatedProductId,
        },
      };
      await dispatch(fbConversionTrack(params));
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (e) {
      logger.error('trackFbCapiAddSwatchToCartEvent failed', { error: e });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);

/**
 * @description 跟踪facebook add to wishlist事件
 * @param payload
 * @param fulfillWithValue
 * @param dispatch
 * @returns
 */
export const trackFacebookAddToWishlistEvent = createAsyncThunk(
  'tracking/trackFbCapiAddToWishlist',
  async (
    payload: { variant: { name: string; sku: string; price: string }; eventId: string },
    { fulfillWithValue, dispatch }
  ) => {
    try {
      if (!payload.variant) {
        return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
      }

      const params = {
        eventName: EVENTS_NAMES_MAP.FB_CAPI_ADD_TO_WISHLIST,
        eventId: payload.eventId,
        customData: {
          value: +payload.variant.price,
          currency: INTL_CURRENCY,
          content_name: payload.variant.name,
          content_ids: [payload.variant.sku],
        },
      };
      await dispatch(fbConversionTrack(params));
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (e) {
      logger.error('trackFbCapiAddToWishlistEvent failed', { error: e });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);

export const trackFacebookProductViewMoreThan3Event = createAsyncThunk(
  'tracking/trackFbCapiProductViewMoreThan3',
  async (_, { fulfillWithValue, dispatch, extra }) => {
    if (!trackingFeatureService.enabledTrackProductPageViewMoreThan3) {
      logger.info('trackFbCapiProductViewMoreThan3Event is not enabled');
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    }
    try {
      const { persistenceHandles } = extra as ExtraArgument;
      const productPageCount = persistenceHandles.pdpViewedCountPerSession.getItem();
      const viewedCount = productPageCount ? Number(productPageCount) : 0;
      if (viewedCount === 3) {
        const params = {
          eventName: EVENTS_NAMES_MAP.FB_CAPI_CUSTOM_PRODUCT_PAGE_VIEW_MORE_THAN_3,
          eventId: getEventRandomId(EVENTS_NAMES_MAP.FB_CAPI_CUSTOM_PRODUCT_PAGE_VIEW_MORE_THAN_3),
          customData: {},
        };
        await dispatch(fbConversionTrack(params));
      }
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (e) {
      logger.error('trackFbCapiCheckoutEvent failed', { error: e });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);

export const trackFacebookCompleteRegistrationEvent = createAsyncThunk(
  'tracking/trackFbCapiCompleteRegistration',
  async (payload: { eventId: string }, { fulfillWithValue, dispatch }) => {
    try {
      const params = {
        eventName: EVENTS_NAMES_MAP.FB_CAPI_COMPLETE_REGISTRATION,
        eventId: payload.eventId,
        customData: {
          value: 0.0,
          currency: INTL_CURRENCY,
          content_name: 'Sign Up',
        },
      };
      await dispatch(fbConversionTrack(params));
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (e) {
      logger.error('trackFbCapiPlaceOrderEvent failed', { error: e });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);

export const trackFacebookNewsletterSubscriptionEvent = createAsyncThunk(
  'tracking/trackFbCapiNewsletterSubscription',
  async (payload: { eventId: string }, { fulfillWithValue, dispatch }) => {
    try {
      const params = {
        eventName: EVENTS_NAMES_MAP.FB_CAPI_COMPLETE_REGISTRATION,
        eventId: payload.eventId,
        customData: {
          value: 0.0,
          currency: INTL_CURRENCY,
          content_name: 'Subscription Footer',
        },
      };
      await dispatch(fbConversionTrack(params));
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (e) {
      logger.error('trackFbCapiNewsletterSubscriptionEvent failed', { error: e });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);

export const trackFacebookActWithSignupEvent = createAsyncThunk(
  'tracking/trackFbCapiActWithSignup',
  async (_, { fulfillWithValue, dispatch }) => {
    try {
      const eventId = getEventRandomId(EVENTS_NAMES_MAP.FB_CAPI_CUSTOM_ACT_WITH_SIGNUP);
      const params = {
        eventName: EVENTS_NAMES_MAP.FB_CAPI_CUSTOM_ACT_WITH_SIGNUP,
        eventId: eventId,
        customData: {},
      };
      await dispatch(fbConversionTrack(params));
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (e) {
      logger.error('trackFbCapiActWithSignupEvent failed', { error: e });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);
